import { useQuery, useMutation } from '@tanstack/react-query';
import { auditApi } from '../services/api';

/**
 * Hook to fetch audit logs with filters
 */
export const useAuditLogs = (filters = {}, options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['auditLogs', filters],
    queryFn: () => auditApi.getLogs(filters),
    enabled,
    staleTime: 30000, // Consider data stale after 30 seconds
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch audit summary statistics
 */
export const useAuditSummary = (startDate, endDate, options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['auditSummary', startDate, endDate],
    queryFn: () => auditApi.getSummary(startDate, endDate),
    enabled,
    staleTime: 60000, // Summary data can be cached longer
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch a single audit log by ID (full details)
 */
export const useAuditLogDetail = (auditId, options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['auditLogDetail', auditId],
    queryFn: () => auditApi.getLogById(auditId),
    enabled: enabled && !!auditId,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to fetch filter options for dropdowns
 */
export const useAuditFilterOptions = (options = {}) => {
  const { enabled = true } = options;

  return useQuery({
    queryKey: ['auditFilterOptions'],
    queryFn: () => auditApi.getFilterOptions(),
    enabled,
    staleTime: 300000, // Filter options don't change often - 5 minutes
    refetchOnWindowFocus: false,
  });
};

/**
 * Hook to export audit logs
 */
export const useExportAuditLogs = () => {
  return useMutation({
    mutationFn: ({ filters, format }) => auditApi.exportLogs(filters, format),
    onSuccess: (data, variables) => {
      // Create a download link for the blob
      const blob = new Blob([data], {
        type: variables.format === 'csv' ? 'text/csv' : 'application/octet-stream',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${variables.format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
  });
};

/**
 * Hook to log frontend events
 */
export const useLogAuditEvent = () => {
  return useMutation({
    mutationFn: (eventData) => auditApi.logEvent(eventData),
    // Don't throw errors for audit logging - it should be fire-and-forget
    onError: (error) => {
      console.warn('Failed to log audit event:', error.message);
    },
  });
};

/**
 * Helper hook to log page views
 * Usage: const logPageView = useLogPageView();
 *        useEffect(() => { logPageView('Dashboard'); }, []);
 */
export const useLogPageView = () => {
  const { mutate } = useLogAuditEvent();

  return (screenName, menuPath = null) => {
    mutate({
      actionType: 'VIEW',
      actionCategory: 'NAVIGATION',
      screenName,
      menuPath,
    });
  };
};

export default useAuditLogs;
